import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Brain, BarChart2, Users, Layers, CheckCircle, MessageSquare, Network, CloudLightning } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer/Footer';
import SectionDivider from '../components/SectionDivider/SectionDivider';
import ProjectFormModal from '../components/ProjectFormModal/ProjectFormModal';
import screenDashboard from '../assets/bpxai-screen-dashboard.jpg';
import screenAbsenteeism from '../assets/bpxai-screen-absenteeism.jpg';
import screenDday from '../assets/bpxai-screen-dday.jpg';
import screenManning from '../assets/bpxai-screen-manning.jpg';
import screenAgentic from '../assets/bpxai-screen-agentic.jpg';
import styles from './BlueprintXAIPage.module.css';

const FEATURES = [
  {
    icon: BarChart2,
    title: 'Live Factory Dashboard',
    body: 'Real-time knit-to-pack flow across all six production stages. See efficiency, headcount, and shortfalls at a glance. Clickable stages narrow the floor board instantly so supervisors act on facts, not guesses.',
    screen: screenDashboard,
    screenAlt: 'Factory Dashboard showing knit-to-pack flow',
  },
  {
    icon: Brain,
    title: 'Multi-Model Absenteeism AI',
    body: '24-hour ahead probabilistic predictions per operator, powered by an ensemble of LightGBM, CatBoost and Prophet. The system fuses 38+ signals: attendance history, commute distance, regional holidays, local weather forecasts, shift patterns and seasonal trends so predictions stay accurate across every calendar week.',
    screen: screenAbsenteeism,
    screenAlt: 'Absenteeism forecast screen',
  },
  {
    icon: Layers,
    title: 'Auto Manning Sheet',
    body: 'Pre-shift baseline assignments generated each morning from the skill matrix, live order backlog and operation bulletins. Three hours of daily supervisor work reduced to a single review step, with every allocation traceable to a data source.',
    screen: screenManning,
    screenAlt: 'Manning sheet screen',
  },
  {
    icon: Users,
    title: 'Agentic D-Day Line Balancing',
    body: 'When absences hit, a multi-agent pipeline scores every gap-candidate pair, weighs skill match, performance history and work-in-progress impact, then surfaces ranked moves with full reasoning. Floor-level decisions that previously took 45 minutes now complete in under 60 seconds.',
    screen: screenDday,
    screenAlt: 'D-Day cell balancing screen',
  },
  {
    icon: MessageSquare,
    title: 'Agentic Control Center: Ask the Floor',
    body: 'Supervisors query live production data in plain language from the same screen. Ask which section is the worst bottleneck, what is driving today\'s output shortfall, or which line has unmanned operations. The agent reasons over the current run and returns answers with source citations, not a link to another dashboard.',
    screen: screenAgentic,
    screenAlt: 'Agentic control center with suggested questions and chat',
  },
];

const OUTCOMES = [
  { value: '< 4%', label: 'Daily capacity loss', baseline: 'vs 7-12% baseline' },
  { value: '< 15 min', label: 'Manning preparation', baseline: 'vs 3 hrs manual' },
  { value: '< 5 min', label: 'Rebalance after absence', baseline: 'vs 45 min reactive' },
  { value: '> 60%', label: 'HIGH-risk prediction precision', baseline: 'vs no forecast' },
];

const AI_CAPABILITIES = [
  {
    icon: Network,
    title: 'Multi-Agent Framework',
    body: 'A coordinated network of specialized agents handles forecasting, assignment scoring, conflict resolution and approval routing in parallel. No single model bottleneck: each agent owns a well-scoped task and passes structured outputs to the next stage.',
  },
  {
    icon: Brain,
    title: 'Multi-Model Prediction Ensemble',
    body: 'LightGBM, CatBoost and Prophet run in parallel for every operator, each contributing a calibrated probability. The ensemble fuses weather, regional public holidays, local commute data and historical patterns into a single high-confidence risk score.',
  },
  {
    icon: MessageSquare,
    title: 'Agentic Chat for Reporting',
    body: 'Supervisors and managers query the floor in plain language. Ask what is driving today efficiency drop, pull a shift summary or compare last week against target. The agent reasons over live production data and returns answers with source citations, not static dashboards.',
  },
  {
    icon: CloudLightning,
    title: 'Live Production Monitoring',
    body: 'Continuous ingestion of operator check-ins, machine states and order progress feeds a real-time anomaly layer. Alerts surface before a shortfall compounds, with an explanation of root cause and an agent-suggested corrective action ready to approve.',
  },
];

const SERVICES_BRIEF = [
  { title: 'AI and LLM Engineering', desc: 'Custom LLM pipelines, RAG architectures and multi-agent systems.' },
  { title: 'Cloud Architecture', desc: 'Azure, AWS and GCP: from design to production deployment.' },
  { title: 'Product Development', desc: 'Full-stack teams from concept to launch, delivered in weeks.' },
  { title: 'Digital Transformation', desc: 'Strategy, execution and change management for enterprise initiatives.' },
];

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); }
    }, { threshold });
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FeatureCard = ({ feature, index }) => {
  const [ref, visible] = useInView();
  const isEven = index % 2 === 0;
  const Icon = feature.icon;

  return (
    <div
      ref={ref}
      className={`${styles.featureCard} ${visible ? styles.featureVisible : ''}`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className={`${styles.featureContent} ${isEven ? styles.contentLeft : styles.contentRight}`}>
        <div className={styles.featureIconWrap}>
          <Icon size={22} />
        </div>
        <h3 className={styles.featureTitle}>{feature.title}</h3>
        <p className={styles.featureBody}>{feature.body}</p>
      </div>
      <div className={`${styles.featureScreen} ${isEven ? styles.screenRight : styles.screenLeft}`}>
        <div className={styles.screenFrame}>
          <div className={styles.screenBar}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
          </div>
          <img src={feature.screen} alt={feature.screenAlt} className={styles.screenImg} />
        </div>
      </div>
    </div>
  );
};

const AICapabilityCard = ({ item, index, visible }) => {
  const Icon = item.icon;
  return (
    <div
      className={`${styles.aiCapCard} ${visible ? styles.aiCapCardVisible : ''}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={styles.aiCapIcon}>
        <Icon size={22} />
      </div>
      <h3 className={styles.aiCapTitle}>{item.title}</h3>
      <p className={styles.aiCapBody}>{item.body}</p>
    </div>
  );
};

const BlueprintXAIPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [featuresRef, featuresVisible] = useInView();
  const [aiRef, aiVisible] = useInView();
  const [outcomesRef, outcomesVisible] = useInView();
  const [servicesRef, servicesVisible] = useInView();
  const navigate = useNavigate();

  return (
    <>
      <main>
        {/* Hero */}
        <section className={`${styles.hero} grid-bg`} id="hero">
          <div className={styles.heroInner}>
            <div className={styles.heroText}>
              <span className={styles.productBadge}>BlueprintX AI</span>
              <h1 className={styles.heroTitle}>
                The Agentic Intelligence Layer for Your Factory Floor
              </h1>
              <p className={styles.heroSub}>
                A multi-agent AI system that predicts absences 24 hours ahead, auto-generates daily manning sheets and rebalances production lines in under 60 seconds. BlueprintX AI replaces reactive guesswork with continuous, explainable machine intelligence.
              </p>
              <div className={styles.heroCtas}>
                <button className={styles.primaryCta} onClick={() => setIsFormOpen(true)}>
                  Request a Demo
                  <ArrowRight size={18} />
                </button>
                <button
                  className="bracket-btn bracket-btn--light"
                  onClick={() => {
                    const el = document.getElementById('features');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  See How It Works
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>

            <div className={styles.heroScreenWrap}>
              <div className={styles.heroScreenFrame}>
                <div className={styles.heroScreenBar}>
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.heroScreenTitle}>BlueprintX AI - Factory Dashboard</span>
                </div>
                <img src={screenDashboard} alt="Factory Dashboard" className={styles.heroScreenImg} />
              </div>
              <div className={styles.heroFloatCard}>
                <span className={styles.floatCardValue}>89.76%</span>
                <span className={styles.floatCardLabel}>Overall Efficiency</span>
              </div>
              <div className={`${styles.heroFloatCard} ${styles.floatCard2}`}>
                <span className={styles.floatCardValue}>6.23%</span>
                <span className={styles.floatCardLabel}>Predicted Absence Rate</span>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className={styles.statsBar}>
            <div className={styles.statItem}>
              <span className={styles.statVal}>562</span>
              <span className={styles.statLbl}>Operators tracked</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statVal}>38+</span>
              <span className={styles.statLbl}>AI prediction signals</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statVal}>&lt; 60s</span>
              <span className={styles.statLbl}>Agentic rebalance time</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.statItem}>
              <span className={styles.statVal}>5</span>
              <span className={styles.statLbl}>User roles and permissions</span>
            </div>
          </div>
        </section>

        <SectionDivider variant="blue-to-cream" />

        {/* AI Architecture strip */}
        <section id="ai" className={styles.aiSection}>
          <div className="container">
            <div
              ref={aiRef}
              className={`${styles.sectionHeader} ${aiVisible ? styles.sectionHeaderVisible : ''}`}
            >
              <span className="section-tag section-tag--dark">AI ARCHITECTURE</span>
              <h2 className="section-title section-title--dark">
                Built on a Multi-Agent, Multi-Model Core
              </h2>
              <p className="section-subtitle section-subtitle--dark">
                BlueprintX AI is not a dashboard with ML bolted on. It is a coordinated system of specialized agents, each trained on manufacturing-specific signals, working together in real time.
              </p>
            </div>
            <div className={styles.aiCapGrid}>
              {AI_CAPABILITIES.map((item, i) => (
                <AICapabilityCard key={item.title} item={item} index={i} visible={aiVisible} />
              ))}
            </div>
          </div>
        </section>

        <SectionDivider variant="cream-to-blue" />

        {/* Feature rows */}
        <section id="features" className={`${styles.featuresSection} grid-bg`}>
          <div className="container">
            <div
              ref={featuresRef}
              className={`${styles.sectionHeader} ${styles.sectionHeaderLight} ${featuresVisible ? styles.sectionHeaderVisible : ''}`}
            >
              <span className="section-tag section-tag--light">THE PRODUCT</span>
              <h2 className="section-title section-title--light">
                Five Screens That Run Your Factory Floor
              </h2>
              <p className="section-subtitle section-subtitle--light">
                Every decision a supervisor faces each shift: from who shows up to where they stand, answered with data and agent reasoning, not intuition.
              </p>
            </div>
          </div>

          <div className={styles.featuresContainer}>
            {FEATURES.map((feature, i) => (
              <FeatureCard key={feature.title} feature={feature} index={i} />
            ))}
          </div>
        </section>

        <SectionDivider variant="blue-to-cream" />

        {/* Outcomes */}
        <section className={styles.outcomesSection} id="outcomes">
          <div className="container">
            <div
              ref={outcomesRef}
              className={`${styles.outcomesHeader} ${outcomesVisible ? styles.outcomesHeaderVisible : ''}`}
            >
              <span className="section-tag section-tag--dark">MEASURED OUTCOMES</span>
              <h2 className="section-title section-title--dark">
                Production Gains You Can Put in a Report
              </h2>
            </div>
            <div className={styles.outcomesGrid}>
              {OUTCOMES.map((item, i) => (
                <div
                  key={item.label}
                  className={`${styles.outcomeCard} ${outcomesVisible ? styles.outcomeCardVisible : ''}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <span className={styles.outcomeValue}>{item.value}</span>
                  <span className={styles.outcomeLabel}>{item.label}</span>
                  <span className={styles.outcomeBaseline}>{item.baseline}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <SectionDivider variant="cream-to-blue" />

        {/* Services overview */}
        <section className={`${styles.servicesSection} grid-bg`} id="also-from-blueprintx">
          <div className="container">
            <div
              ref={servicesRef}
              className={`${styles.servicesHeader} ${servicesVisible ? styles.servicesHeaderVisible : ''}`}
            >
              <span className="section-tag section-tag--light">ALSO FROM BLUEPRINTX</span>
              <h2 className="section-title section-title--light">
                The Full Execution Stack
              </h2>
              <p className="section-subtitle section-subtitle--light">
                BlueprintX AI is one product in a broader portfolio. We build, deploy and run AI, cloud and digital initiatives end-to-end for enterprises that need more than a vendor.
              </p>
            </div>

            <div className={styles.servicesBriefGrid}>
              {SERVICES_BRIEF.map((svc, i) => (
                <div
                  key={svc.title}
                  className={`${styles.serviceBriefCard} ${servicesVisible ? styles.serviceBriefCardVisible : ''}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <CheckCircle size={18} className={styles.serviceCheckIcon} />
                  <div>
                    <h4 className={styles.serviceBriefTitle}>{svc.title}</h4>
                    <p className={styles.serviceBriefDesc}>{svc.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.servicesCta}>
              <button
                className="bracket-btn bracket-btn--light"
                onClick={() => { navigate('/services'); window.scrollTo({ top: 0 }); }}
              >
                Explore All Services
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>

        {/* CTA Band */}
        <section className={`${styles.ctaBand} grid-bg`}>
          <div className={styles.ctaBandInner}>
            <h2 className={styles.ctaBandTitle}>
              Ready to stop managing your floor by instinct?
            </h2>
            <p className={styles.ctaBandSub}>
              See BlueprintX AI running on your factory's actual structure. No slides, no sandbox.
            </p>
            <button className={styles.ctaBandBtn} onClick={() => setIsFormOpen(true)}>
              Book a Walkthrough
              <ArrowRight size={18} />
            </button>
          </div>
        </section>
      </main>

      <SectionDivider variant="blue-to-dark" />
      <Footer />
      <ProjectFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </>
  );
};

export default BlueprintXAIPage;
